import { useEffect, useId, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import {
  AdjustmentsHorizontalIcon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';
import { trackEvent } from '@/utils/analytics';
import type { AnalyticsConfig } from './analyticsConfig';
import { getAvailableChartableColumns, type ChartableColumn } from './chartableColumns';
import { getColumnChartTypeOptions, type ColumnChartType } from './columnChartTypes';
import { filterMetricsByCapability, type MetricDefinition } from './metricCatalog';
import type { AssociationRow } from './analyticsTypes';
import {
  getPanelDefinition,
  getPanelLabel,
  isPresetSummaryCard,
  MAX_COLUMN_CHARTS,
  type ColumnChartParams,
  type DashboardLayoutState,
  type MetricScatterParams,
  type PanelParams,
  type ScoreDistributionParams,
  type TopEntitiesParams,
} from './widgetRegistry';

interface DashboardCustomizerProps {
  config: AnalyticsConfig;
  catalog: MetricDefinition[];
  chartableColumns: ChartableColumn[];
  data: AssociationRow[];
  layout: DashboardLayoutState;
  isCustomized: boolean;
  columnChartCount: number;
  maxColumnCharts?: number;
  onTogglePanel: (panelId: string) => void;
  onToggleCard: (cardId: string) => void;
  onMovePanel: (panelId: string, direction: 'up' | 'down') => void;
  onReorderPanel: (fromIndex: number, toIndex: number) => void;
  onUpdatePanelParams: (panelId: string, params: Partial<PanelParams>) => void;
  onUpdateCardMetric: (cardId: string, metric: string | undefined) => void;
  onAddColumnChart: (field: string) => boolean;
  onAddColumnCharts: (fields: string[]) => number;
  onRemovePanel: (panelId: string) => void;
  onReset: () => void;
}

function getCardDisplayLabel(
  cardId: string,
  config: AnalyticsConfig,
  catalog: MetricDefinition[],
  metricOverride?: string,
): string {
  const card = config.summaryCards.find(entry => entry.id === cardId);
  if (!card) return cardId;
  if (metricOverride && !isPresetSummaryCard(cardId)) {
    const metric = catalog.find(entry => entry.field === metricOverride || entry.id === metricOverride);
    if (metric) return `Average ${metric.label}`;
  }
  return typeof card.label === 'function' ? card.label(new Date().getFullYear()) : card.label;
}

function panelDisplayLabel(
  panel: DashboardLayoutState['panels'][number],
  config: AnalyticsConfig,
  chartableColumns: ChartableColumn[],
): string {
  if (panel.type === 'columnChart') {
    const field = (panel.params as ColumnChartParams).field;
    return getPanelLabel(panel.type, config, { field, chartableColumns });
  }
  return getPanelDefinition(panel.type).label;
}

export function DashboardCustomizer({
  config,
  catalog,
  chartableColumns,
  data,
  layout,
  isCustomized,
  columnChartCount,
  maxColumnCharts = MAX_COLUMN_CHARTS,
  onTogglePanel,
  onToggleCard,
  onMovePanel,
  onReorderPanel,
  onUpdatePanelParams,
  onUpdateCardMetric,
  onAddColumnChart,
  onAddColumnCharts,
  onRemovePanel,
  onReset,
}: DashboardCustomizerProps) {
  const [open, setOpen] = useState(false);
  const [expandedPanelId, setExpandedPanelId] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const aggregableMetrics = filterMetricsByCapability(catalog, 'aggregable');
  const rankableMetrics = filterMetricsByCapability(catalog, 'rankable');
  const axisMetrics = filterMetricsByCapability(catalog, 'axis');

  const usedFields = useMemo(() => {
    const fields = new Set<string>();
    layout.panels.forEach(panel => {
      if (panel.type !== 'columnChart') return;
      const field = (panel.params as ColumnChartParams).field;
      if (field) fields.add(field);
    });
    return fields;
  }, [layout.panels]);

  const availableToAdd = useMemo(() => {
    return getAvailableChartableColumns(chartableColumns, data)
      .filter(column => !usedFields.has(column.field))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [chartableColumns, data, usedFields]);

  const atLimit = columnChartCount >= maxColumnCharts;

  useEffect(() => {
    const allowed = new Set(availableToAdd.map(column => column.field));
    setSelectedFields(prev => prev.filter(field => allowed.has(field)));
  }, [availableToAdd]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        const trigger = rootRef.current?.querySelector<HTMLElement>('button');
        trigger?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const announce = (message: string) => {
    setLiveMessage('');
    requestAnimationFrame(() => setLiveMessage(message));
  };

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      trackEvent({
        name: 'dashboard_customize_open',
        buttonId: 'dashboard_customize_open',
        page: config.tab,
        section: 'analytics',
        feature: 'dashboard_customization',
        eventCategory: 'dashboard_customization',
      });
    }
  };

  const handleTogglePanel = (panelIdValue: string, currentlyHidden: boolean, type: string, label: string) => {
    onTogglePanel(panelIdValue);
    trackEvent({
      name: 'dashboard_widget_toggle',
      buttonId: `dashboard_widget_toggle_${type}`,
      page: config.tab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: { widget_type: type, visible: currentlyHidden },
    });
    announce(`${label} ${currentlyHidden ? 'shown' : 'hidden'}`);
  };

  const handleMove = (panelIdValue: string, direction: 'up' | 'down', type: string, index: number) => {
    const to = direction === 'up' ? index - 1 : index + 1;
    onMovePanel(panelIdValue, direction);
    trackEvent({
      name: 'dashboard_widget_reorder',
      buttonId: `dashboard_widget_reorder_${type}`,
      page: config.tab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: { widget_type: type, from: index, to, method: 'button' },
    });
    announce(`Panel moved to position ${to + 1}`);
  };

  const handleDragStart = (index: number) => (event: DragEvent) => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDrop = (toIndex: number) => (event: DragEvent) => {
    event.preventDefault();
    const fromIndex = dragIndex ?? Number(event.dataTransfer.getData('text/plain'));
    setDragIndex(null);
    if (!Number.isFinite(fromIndex) || fromIndex === toIndex) return;
    const panel = layout.panels[fromIndex];
    if (!panel) return;
    onReorderPanel(fromIndex, toIndex);
    trackEvent({
      name: 'dashboard_widget_reorder',
      buttonId: `dashboard_widget_reorder_${panel.type}`,
      page: config.tab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: {
        widget_type: panel.type,
        from: fromIndex,
        to: toIndex,
        method: 'drag',
      },
    });
    announce(`${panelDisplayLabel(panel, config, chartableColumns)} moved to position ${toIndex + 1}`);
  };

  const trackMetricChange = (widgetType: string, param: string, metric: string) => {
    trackEvent({
      name: 'dashboard_metric_change',
      buttonId: `dashboard_metric_change_${widgetType}_${param}`,
      page: config.tab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: { widget_type: widgetType, param, metric },
    });
  };

  const remainingSlots = Math.max(0, maxColumnCharts - columnChartCount);

  const toggleField = (field: string) => {
    setSelectedFields(prev => {
      if (prev.includes(field)) return prev.filter(entry => entry !== field);
      if (prev.length >= remainingSlots) return prev;
      return [...prev, field];
    });
  };

  const handleSelectFill = () => {
    setSelectedFields(availableToAdd.slice(0, remainingSlots).map(column => column.field));
  };

  const handleAddChart = () => {
    if (!selectedFields.length || atLimit) return;
    const fields = selectedFields.slice(0, remainingSlots);
    const added = onAddColumnCharts(fields);
    if (!added) return;
    trackEvent({
      name: 'dashboard_chart_add',
      buttonId: 'dashboard_chart_add_multi',
      page: config.tab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: {
        fields,
        count: added,
      },
    });
    announce(`Added ${added} chart${added === 1 ? '' : 's'}`);
    setSelectedFields([]);
  };

  const handleRemove = (panelIdValue: string, field: string) => {
    onRemovePanel(panelIdValue);
    trackEvent({
      name: 'dashboard_chart_remove',
      buttonId: 'dashboard_chart_remove',
      page: config.tab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: { field },
    });
    announce('Chart removed');
  };

  const handleReset = () => {
    onReset();
    trackEvent({
      name: 'dashboard_layout_reset',
      buttonId: 'dashboard_layout_reset',
      page: config.tab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: { scope: 'all' },
    });
    announce('Dashboard layout reset to default');
  };

  const handlePopoverKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab' && popoverRef.current) {
      const focusable = popoverRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div
      className={`gda-analytics-customizer${isCustomized ? ' gda-analytics-customizer--active' : ''}`}
      ref={rootRef}
    >
      <SharedButton
        variant="secondaryOutline"
        size="xs"
        className="gda-analytics-header__download"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={isCustomized ? 'Customize dashboard (custom layout)' : 'Customize dashboard'}
        title={isCustomized ? 'Customize · Custom' : 'Customize'}
        onClick={handleOpen}
        iconLeft={<AdjustmentsHorizontalIcon className="h-4 w-4" />}
        iconMargin="gda-analytics-header__action-icon"
      >
        <span className="gda-analytics-header__action-label">
          {isCustomized ? 'Customize · Custom' : 'Customize'}
        </span>
      </SharedButton>

      {open ? (
        <div
          ref={popoverRef}
          id={panelId}
          className="gda-analytics-customizer__popover"
          role="dialog"
          aria-label="Customize analytics dashboard"
          onKeyDown={handlePopoverKeyDown}
        >
          <div className="gda-analytics-customizer__header">
            <div className="gda-analytics-customizer__header-copy">
              <h3 className="gda-analytics-customizer__title">Customize dashboard</h3>
              <p className="gda-analytics-customizer__hint">
                Pick what to see. Layout is saved on this browser.
              </p>
            </div>
            <button
              type="button"
              className="gda-analytics-customizer__close"
              aria-label="Close customize panel"
              onClick={() => setOpen(false)}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="gda-analytics-customizer__body">
            <section
              className="gda-analytics-customizer__section gda-analytics-customizer__section--add"
              aria-label="Add chart"
            >
              <div className="gda-analytics-customizer__section-head">
                <h4 className="gda-analytics-customizer__section-title">Add chart</h4>
                <span className="gda-analytics-customizer__count">
                  {columnChartCount}/{maxColumnCharts}
                </span>
              </div>
              <p className="gda-analytics-customizer__field-label">Columns from table</p>
              <ul className="gda-analytics-customizer__multi" aria-label="Columns from table">
                {availableToAdd.length === 0 ? (
                  <li className="gda-analytics-customizer__hint" style={{ padding: '0.35rem 0.45rem' }}>
                    {atLimit
                      ? 'Limit reached. Remove a chart to add another.'
                      : 'All available columns are already on the dashboard.'}
                  </li>
                ) : (
                  availableToAdd.map(column => (
                    <li key={column.id}>
                      <label className="gda-analytics-customizer__multi-item">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(column.field)}
                          disabled={
                            atLimit ||
                            (!selectedFields.includes(column.field) &&
                              selectedFields.length >= remainingSlots)
                          }
                          onChange={() => toggleField(column.field)}
                        />
                        <span>
                          {column.label}
                          <span className="gda-analytics-customizer__hint"> · {column.kind}</span>
                        </span>
                      </label>
                    </li>
                  ))
                )}
              </ul>
              <div className="gda-analytics-customizer__multi-actions">
                <SharedButton
                  variant="secondaryOutline"
                  size="xs"
                  className="gda-analytics-customizer__fill-btn"
                  disabled={atLimit || availableToAdd.length === 0}
                  onClick={handleSelectFill}
                >
                  Fill remaining ({remainingSlots})
                </SharedButton>
                <SharedButton
                  variant="secondaryOutline"
                  size="xs"
                  className="gda-analytics-customizer__add-btn"
                  disabled={!selectedFields.length || atLimit}
                  onClick={handleAddChart}
                  iconLeft={<PlusIcon className="h-4 w-4" />}
                >
                  Add selected ({selectedFields.length})
                </SharedButton>
              </div>
              <p className="gda-analytics-customizer__hint">
                Multi-select up to free slots. Full “select all” would flood the board — use Fill remaining
                instead.
              </p>
            </section>

            <section className="gda-analytics-customizer__section" aria-label="Summary metrics">
              <h4 className="gda-analytics-customizer__section-title">Summary metrics</h4>
              <ul className="gda-analytics-customizer__list">
                {layout.cards.map(card => {
                  const label = getCardDisplayLabel(card.id, config, catalog, card.metric);
                  return (
                    <li key={card.id} className="gda-analytics-customizer__row">
                      <label className="gda-analytics-customizer__check">
                        <input
                          type="checkbox"
                          checked={!card.hidden}
                          onChange={() => {
                            onToggleCard(card.id);
                            trackEvent({
                              name: 'dashboard_widget_toggle',
                              buttonId: `dashboard_card_toggle_${card.id}`,
                              page: config.tab,
                              section: 'analytics',
                              feature: 'dashboard_customization',
                              eventCategory: 'dashboard_customization',
                              additionalParams: {
                                widget_type: 'summaryCard',
                                visible: card.hidden,
                              },
                            });
                          }}
                        />
                        <span>{label}</span>
                      </label>
                      {!isPresetSummaryCard(card.id) && aggregableMetrics.length > 0 ? (
                        <label className="gda-analytics-customizer__field">
                          <span className="gda-analytics-customizer__field-label">Metric</span>
                          <select
                            value={card.metric ?? ''}
                            onChange={event => {
                              const value = event.target.value || undefined;
                              onUpdateCardMetric(card.id, value);
                              if (value) trackMetricChange('summaryCard', 'metric', value);
                            }}
                          >
                            <option value="">Default</option>
                            {aggregableMetrics.map(metric => (
                              <option key={metric.id} value={metric.field}>
                                {metric.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="gda-analytics-customizer__section" aria-label="Panels">
              <h4 className="gda-analytics-customizer__section-title">Panels</h4>
              <ul className="gda-analytics-customizer__list">
                {layout.panels.map((panel, index) => {
                  const label = panelDisplayLabel(panel, config, chartableColumns);
                  const expanded = expandedPanelId === panel.id;
                  const topParams = panel.params as TopEntitiesParams;
                  const distParams = panel.params as ScoreDistributionParams;
                  const scatterParams = panel.params as MetricScatterParams;
                  const columnParams = panel.params as ColumnChartParams;
                  const isUserChart = panel.type === 'columnChart';

                  return (
                    <li
                      key={panel.id}
                      className="gda-analytics-customizer__row gda-analytics-customizer__row--panel"
                      draggable
                      onDragStart={handleDragStart(index)}
                      onDragOver={event => event.preventDefault()}
                      onDrop={handleDrop(index)}
                    >
                      <div className="gda-analytics-customizer__panel-main">
                        <span className="gda-analytics-customizer__drag" aria-hidden="true">
                          <Bars3Icon className="h-4 w-4" />
                        </span>
                        <label className="gda-analytics-customizer__check">
                          <input
                            type="checkbox"
                            checked={!panel.hidden}
                            onChange={() => handleTogglePanel(panel.id, panel.hidden, panel.type, label)}
                          />
                          <span>{label}</span>
                        </label>
                        <div className="gda-analytics-customizer__moves">
                          <button
                            type="button"
                            className="gda-analytics-customizer__icon-btn"
                            aria-label={`Move ${label} up`}
                            disabled={index === 0}
                            onClick={() => handleMove(panel.id, 'up', panel.type, index)}
                          >
                            <ChevronUpIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="gda-analytics-customizer__icon-btn"
                            aria-label={`Move ${label} down`}
                            disabled={index === layout.panels.length - 1}
                            onClick={() => handleMove(panel.id, 'down', panel.type, index)}
                          >
                            <ChevronDownIcon className="h-4 w-4" />
                          </button>
                          {(panel.type === 'topEntities' ||
                            panel.type === 'scoreDistribution' ||
                            panel.type === 'metricScatter' ||
                            panel.type === 'columnChart') && (
                            <button
                              type="button"
                              className="gda-analytics-customizer__text-btn"
                              aria-expanded={expanded}
                              onClick={() => setExpandedPanelId(expanded ? null : panel.id)}
                            >
                              Data
                            </button>
                          )}
                          <button
                            type="button"
                            className="gda-analytics-customizer__icon-btn"
                            aria-label={`Remove ${label}`}
                            onClick={() =>
                              handleRemove(panel.id, isUserChart ? columnParams.field : panel.type)
                            }
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {expanded && panel.type === 'topEntities' ? (
                        <label className="gda-analytics-customizer__field">
                          <span className="gda-analytics-customizer__field-label">Rank by</span>
                          <select
                            value={topParams.metric}
                            onChange={event => {
                              onUpdatePanelParams(panel.id, { metric: event.target.value });
                              trackMetricChange(panel.type, 'metric', event.target.value);
                            }}
                          >
                            {rankableMetrics.map(metric => (
                              <option key={metric.id} value={metric.field}>
                                {metric.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}

                      {expanded && panel.type === 'scoreDistribution' ? (
                        <label className="gda-analytics-customizer__field">
                          <span className="gda-analytics-customizer__field-label">Distribute by</span>
                          <select
                            value={distParams.metric}
                            onChange={event => {
                              onUpdatePanelParams(panel.id, { metric: event.target.value });
                              trackMetricChange(panel.type, 'metric', event.target.value);
                            }}
                          >
                            {axisMetrics.map(metric => (
                              <option key={metric.id} value={metric.field}>
                                {metric.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}

                      {expanded && panel.type === 'metricScatter' ? (
                        <div className="gda-analytics-customizer__fields">
                          <label className="gda-analytics-customizer__field">
                            <span className="gda-analytics-customizer__field-label">X axis</span>
                            <select
                              value={scatterParams.xMetric}
                              onChange={event => {
                                onUpdatePanelParams(panel.id, { xMetric: event.target.value });
                                trackMetricChange(panel.type, 'xMetric', event.target.value);
                              }}
                            >
                              {axisMetrics.map(metric => (
                                <option key={metric.id} value={metric.field}>
                                  {metric.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="gda-analytics-customizer__field">
                            <span className="gda-analytics-customizer__field-label">Y axis</span>
                            <select
                              value={scatterParams.yMetric}
                              onChange={event => {
                                onUpdatePanelParams(panel.id, { yMetric: event.target.value });
                                trackMetricChange(panel.type, 'yMetric', event.target.value);
                              }}
                            >
                              {axisMetrics.map(metric => (
                                <option key={metric.id} value={metric.field}>
                                  {metric.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ) : null}

                      {expanded && panel.type === 'columnChart' ? (
                        <label className="gda-analytics-customizer__field">
                          <span className="gda-analytics-customizer__field-label">Chart type</span>
                          <select
                            value={columnParams.chartType ?? getColumnChartTypeOptions()[0]?.value}
                            onChange={event => {
                              const nextType = event.target.value as ColumnChartType;
                              onUpdatePanelParams(panel.id, { chartType: nextType });
                              trackEvent({
                                name: 'dashboard_chart_type_change',
                                buttonId: 'dashboard_chart_type_change',
                                page: config.tab,
                                section: 'analytics',
                                feature: 'dashboard_customization',
                                eventCategory: 'dashboard_customization',
                                additionalParams: {
                                  field: columnParams.field,
                                  chart_type: nextType,
                                  source: 'customizer',
                                },
                              });
                            }}
                          >
                            {getColumnChartTypeOptions().map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>

          <div className="gda-analytics-customizer__footer">
            <SharedButton variant="secondaryOutline" size="xs" disabled={!isCustomized} onClick={handleReset}>
              Reset to default
            </SharedButton>
          </div>
        </div>
      ) : null}

      <div className="sr-only" aria-live="polite">
        {liveMessage}
      </div>
    </div>
  );
}
